import React from "react";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { FinancialConnectionProviderLabel } from "@common/constants";
import {
    FinancialAccount,
    FinancialAccountSourceType,
} from "@common/types/financialAccount";
import { fullDateFormatWithTime } from "@common/helpers/date";
import { format } from "date-fns";
// oxlint-disable-next-line no-restricted-imports -- Legacy table migration/demo — do not copy into new code.
import { GridTableRow } from "@design-system/Tables/GridTable/GridTable";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useExtractMergedAccountMutation } from "@api/financialAccount.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import styles from "./MergedAccountsDetails.module.scss";

interface Props {
    integrationAccount: IntegrationAccount;
    financialAccount: FinancialAccount;
    extractingOtherAccount: boolean;
    onExtractStarted: () => void;
    onExtractFinished: () => void;
}

export const MergedAccountDetails: React.FC<Props> = ({
    integrationAccount,
    financialAccount,
    extractingOtherAccount,
    onExtractStarted,
    onExtractFinished,
}) => {
    const connectedAt = integrationAccount.createdAt
        ? ` at ${format(integrationAccount.createdAt, fullDateFormatWithTime)}`
        : "";
    const connectedThrough = integrationAccount.connection?.connectionProvider
        ? ` through ${FinancialConnectionProviderLabel[integrationAccount.connection?.connectionProvider]}`
        : "";

    const extractMutation = useExtractMergedAccountMutation();
    const { toast } = useToaster();

    const extract = async () => {
        if (!financialAccount.entityId) {
            return;
        }

        onExtractStarted();
        await extractMutation.mutateAsync({
            integrationAccountToExtractId: integrationAccount.id,
            financialAccountId: financialAccount.id,
            entityId: financialAccount.entityId,
        });
        onExtractFinished();

        toast("Account extracted");
    };

    const isImported =
        integrationAccount.sourceType ===
        FinancialAccountSourceType.FILE_IMPORT;

    return (
        <GridTableRow className={styles.row}>
            <div>
                {isImported ? "Imported" : "Connected"}
                {connectedAt}
                {connectedThrough}
            </div>
            <div>
                <ButtonWithLoader
                    variant="secondary"
                    size="sm"
                    loading={extractMutation.isPending}
                    disabled={
                        extractingOtherAccount && !extractMutation.isPending
                    }
                    onClick={extract}
                >
                    Extract
                </ButtonWithLoader>
            </div>
        </GridTableRow>
    );
};
